import { nanoid } from "nanoid";
import { curry } from "ramda";

import type { ConsentTypes, NetworkError } from "@solvari/utils";

import {
  asyncThrottle,
  getEnv,
  getSolvariSessionId,
  isBot,
  isError,
  isSupportedBrowser,
  networkErrorToSentry,
  sSessionStorage,
  throttle,
} from "@solvari/utils";

import type { MarketingParams } from "@/events/marketingParams";

import { getArgusEvents } from "@/client/thinClient";
import { argusTrack } from "@/service/argus.api";

/* eslint-disable @typescript-eslint/naming-convention */
type ArgusEvents = {
  cookie_consent: { consentTypes: ConsentTypes };
  form_submit: {
    id: number | string;
    type: "contact" | "registration";
  };
  generate_lead: { leadId: string };
  google_optimize: {
    experimentId: string;
    variantId: number | string;
  };
  init: { userAgent: string };
  marketing_params: MarketingParams;
  page_view: { pageType: "page" | "referrer"; url: string };
};
/* eslint-enable @typescript-eslint/naming-convention */

type ArgusEvent<Type extends keyof ArgusEvents = keyof ArgusEvents> = {
  createdAt: string;
  data: ArgusEvents[Type];
  status: "pending" | "synced" | "unsynced";
  syncAttempts: string[];
  type: Type;
  uuid: string;
};

function setEvents(mapper: (event: ArgusEvent) => ArgusEvent) {
  sSessionStorage.updateItem(
    "argusEvents",
    (events) => events?.map(mapper) ?? [],
  );
}

const setEventPending = curry((syncAttemptId: string, event: ArgusEvent) => {
  if (event.status !== "synced") {
    return {
      ...event,
      status: "pending",
      syncAttempts: [...event.syncAttempts, syncAttemptId],
    } satisfies ArgusEvent;
  }
  return event;
});

const setEventSynced = curry((syncAttemptId: string, event: ArgusEvent) => {
  if (event.syncAttempts.includes(syncAttemptId)) {
    return { ...event, status: "synced" } satisfies ArgusEvent;
  }
  return event;
});

const setEventUnsynced = curry((syncAttemptId: string, event: ArgusEvent) => {
  if (
    event.status === "pending" &&
    event.syncAttempts.includes(syncAttemptId)
  ) {
    return { ...event, status: "unsynced" } satisfies ArgusEvent;
  }
  return event;
});

function shouldRunClientOnly() {
  return (
    isBot() ||
    !isSupportedBrowser() ||
    // TODO: remove when argus runs locally
    getEnv().config.envType === "local"
  );
}

async function backendSync(): Promise<NetworkError | undefined> {
  if (shouldRunClientOnly()) {
    return;
  }

  const eventsToSync = getArgusEvents()?.filter(
    (event) => event.status !== "synced",
  );
  if (!eventsToSync?.length) {
    return;
  }

  const syncAttemptId = nanoid(10);

  setEvents(setEventPending(syncAttemptId));

  const result = await argusTrack(eventsToSync, getSolvariSessionId()!);

  if (isError(result)) {
    // Backend might be down, reset the pending events to unsynced to be tried again on the next attempt
    setEvents(setEventUnsynced(syncAttemptId));

    networkErrorToSentry(result, { tags: { project: "argus-fe" } });
    return result;
  }

  setEvents(setEventSynced(syncAttemptId));
  return undefined;
}

const scheduleBackendSync = throttle(asyncThrottle(backendSync), 1000);

export { scheduleBackendSync, shouldRunClientOnly };
export type { ArgusEvent, ArgusEvents };
